import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import { endpointLink } from '../index';

// Types for notification preferences
export interface NotificationResponse {
  success: boolean;
  preferences?: any;
  message?: string;
  error?: string;
}

// Custom hook to resend email verification (does not confirm)
export const useResendEmailVerification = () => {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const queryClient = useQueryClient();

  const getToken = async (): Promise<string> => {
    try {
      return await getAccessTokenSilently({
        authorizationParams: {
          audience: "https://odp.golf/audience",
          scope: "openid profile email offline_access",
        },
      });
    } catch (error: any) {
      if (String(error?.message || "").includes('Missing Refresh Token')) {
        await loginWithRedirect({
          authorizationParams: {
            audience: "https://odp.golf/audience",
            scope: "openid profile email offline_access",
            prompt: 'login'
          }
        });
      }
      throw error;
    }
  };

  return useMutation({
    mutationFn: async (): Promise<NotificationResponse> => {
      const token = await getToken();
      const response = await axios.post<NotificationResponse>(`${endpointLink}user/notifications/resend-email`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notificationPreferences'] });
    }
  });
};

// Custom hook to resend SMS verification
export const useResendSMSVerification = () => {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const queryClient = useQueryClient();

  const getToken = async (): Promise<string> => {
    try {
      return await getAccessTokenSilently({
        authorizationParams: {
          audience: "https://odp.golf/audience",
          scope: "openid profile email offline_access",
        },
      });
    } catch (error: any) {
      if (String(error?.message || "").includes('Missing Refresh Token')) {
        await loginWithRedirect({
          authorizationParams: {
            audience: "https://odp.golf/audience",
            scope: "openid profile email offline_access",
            prompt: 'login'
          }
        });
      }
      throw error;
    }
  };

  return useMutation({
    mutationFn: async (): Promise<NotificationResponse> => {
      const token = await getToken();
      try {
        const response = await axios.post<NotificationResponse>(`${endpointLink}user/notifications/resend-sms`, {}, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        return response.data;
      } catch (error: any) {
        // If it's a 200 response with success:false, return it
        if (error.response?.status === 200) {
          return error.response.data;
        }
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notificationPreferences'] });
    }
  });
};
